import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

import { en } from "@/common/en/messages";
import { fr } from "@/common/fr/messages";

interface I18nContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: Record<string, string>) => string;
  isRTL: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const translations = { en, fr };
const rtlLanguages = ["ar", "he", "ur"];

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState(
    () => localStorage.getItem("app_language") || "en",
  );

  const [currentTranslations, setCurrentTranslations] = useState(
    translations.en,
  );

  const t = (key: string, params?: Record<string, string>): string => {
    let translation = currentTranslations[key] || translations.en[key] || key;

    if (params) {
      Object.entries(params).forEach(([param, value]) => {
        translation = translation.replace(`{{${param}}}`, value);
      });
    }

    return translation;
  };

  useEffect(() => {
    const lang = language;
    setCurrentTranslations(translations[lang] || translations.en);

    // Set document language and direction
    document.documentElement.lang = lang;
    document.documentElement.dir = rtlLanguages.includes(lang) ? "rtl" : "ltr";
  }, [language]);

  return (
    <I18nContext.Provider
      value={{
        language: language,
        setLanguage,
        t,
        isRTL: rtlLanguages.includes(language),
      }}
    >
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
}
